import { Pressable, StyleSheet, View } from 'react-native';

import { assertHitOnLayout } from '../a11y';
import { Icon } from '../icons/Icon';
import { colors, hit, radii, strokes } from '../tokens';

export interface SquareButtonProps {
  /** approve = wasabi ✓ with the 2px #170909 border; deny = white × with a 2px slate border. Flat — no plate on the board. */
  kind: 'approve' | 'deny';
  /**
   * **QUEUED: the parent has dealt with this and the server has not agreed yet** (board P29). The tick is drawn
   * in the quiet grey rather than wasabi — `2.5px #9AA5B4` on `#EEF3F7` at 55% — *"so the difference between
   * 'I have dealt with this' and 'the server agreed' is visible without a second control."*
   *
   * It is not `disabled`: disabled means "you may not", and this means "you already did, and it is on its
   * way". The row is inert either way, which is why one prop rather than two.
   */
  queued?: boolean;
  onPress?: () => void;
  /** Drawn size (44 on the board; child mode passes 48). The Pressable box is max(size, minHit). */
  size?: number;
  minHit?: number;
  disabled?: boolean;
  accessibilityLabel: string;
  testID?: string;
}

/** The per-row approve / deny squares on the 1a card and P07. */
export function SquareButton({
  kind,
  queued = false,
  onPress,
  size = 44,
  minHit = hit.parent,
  disabled,
  accessibilityLabel,
  testID,
}: SquareButtonProps) {
  const box = Math.max(size, minHit);
  const approve = kind === 'approve';
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ disabled: Boolean(disabled) }}
      onLayout={assertHitOnLayout(`SquareButton.${kind}`, minHit)}
      testID={testID}
      style={({ pressed }) => [
        styles.box,
        { width: box, height: box },
        disabled && styles.dim,
        pressed && styles.pressed,
      ]}
    >
      <View
        style={[
          styles.face,
          {
            width: size,
            height: size,
            backgroundColor: queued ? colors.neutral : approve ? colors.wasabi : colors.white,
            borderColor: queued ? colors.muted : approve ? colors.ctaBorder : colors.slate,
          },
        ]}
      >
        <Icon
          name={approve ? 'check' : 'x'}
          size={18}
          color={queued ? colors.muted : approve ? colors.ink : colors.slate}
          strokeWidth={3}
        />
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  box: { alignItems: 'center', justifyContent: 'center' },
  face: {
    borderRadius: radii.input,
    borderWidth: strokes.playful,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pressed: { opacity: 0.8 },
  dim: { opacity: 0.5 },
});
